import React from 'react';

// ════════════════════════════════════════════════════════════════════════
// GuestIcon — avatar de usuário não autenticado («Convidado»)
// ────────────────────────────────────────────────────────────────────────
// Substitui a inicial do usuário (ex.: "M") nos círculos de avatar quando
// não há sessão. Mesmo idioma dos ícones de produto: grade 24, traço 1.6,
// cantos arredondados, herda currentColor.
//
// Variações (prop `variant`, padrão 'interrogacao' — opção C escolhida):
//   silhueta       A · busto clássico
//   tracejada      B · busto com traço tracejado
//   interrogacao   C · busto + "?" (padrão)
//   cracha         D · crachá de visitante
//   login          E · porta com seta (ação de entrar)
//   mascara        F · antifaz anônimo
// ════════════════════════════════════════════════════════════════════════

export type GuestIconVariant =
  | 'silhueta'
  | 'tracejada'
  | 'interrogacao'
  | 'cracha'
  | 'login'
  | 'mascara';

const VARIANT_PATHS: Record<GuestIconVariant, React.ReactNode> = {
  silhueta: (
    <>
      <circle cx="12" cy="8.2" r="3.4" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
    </>
  ),
  tracejada: (
    <>
      <circle cx="12" cy="8.2" r="3.4" strokeDasharray="2.4 2.6" />
      <path d="M5 19.5a7 7 0 0 1 14 0" strokeDasharray="2.4 2.6" />
    </>
  ),
  interrogacao: (
    <>
      <circle cx="12" cy="8.2" r="3.4" />
      <path d="M5 19.5a7 7 0 0 1 14 0" />
      <path d="M17.6 4.2a1.9 1.9 0 1 1 2.6 1.8c-.7.3-1 .7-1 1.4" />
      <circle cx="19.2" cy="9.6" r=".7" fill="currentColor" stroke="none" />
    </>
  ),
  cracha: (
    <>
      <rect x="6" y="6.5" width="12" height="14" rx="2" />
      <path d="M10 6.5V5a2 2 0 0 1 4 0v1.5" />
      <circle cx="12" cy="11.5" r="1.8" />
      <path d="M9 17a3.2 3.2 0 0 1 6 0" />
    </>
  ),
  login: (
    <>
      <path d="M13 4h4a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2h-4" />
      <path d="M4 12h9" />
      <path d="M10 8.5 13.5 12 10 15.5" />
    </>
  ),
  mascara: (
    <>
      <path d="M4 11.5 5.6 5.8A1.8 1.8 0 0 1 7.9 4.6l.9.3a8 8 0 0 0 6.4 0l.9-.3a1.8 1.8 0 0 1 2.3 1.2L20 11.5" />
      <path d="M3 11.5h18" />
      <circle cx="7.8" cy="15.5" r="2.6" />
      <circle cx="16.2" cy="15.5" r="2.6" />
      <path d="M10.4 15.5h3.2" />
    </>
  ),
};

export interface GuestIconProps {
  variant?: GuestIconVariant;
  size?: number;
  strokeWidth?: number;
  color?: string;
  style?: React.CSSProperties;
}

export function GuestIcon({
  variant = 'interrogacao',
  size = 14,
  strokeWidth = 1.8,
  color,
  style,
}: GuestIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color, display: 'block', flex: '0 0 auto', ...style }}
      aria-hidden="true"
    >
      {VARIANT_PATHS[variant]}
    </svg>
  );
}

// ── GuestAvatar ───────────────────────────────────────────────────────────
// O círculo pronto, nos três tons de avatar do app. Drop-in no lugar do
// <div>M</div> atual:
//   <GuestAvatar tone="ink" size={26} />                       // topbar
//   <GuestAvatar tone="soft" size={26} />                      // sidebar
//   <GuestAvatar tone="empty" size={30} />                     // estado vazio

export type GuestAvatarTone = 'ink' | 'soft' | 'empty' | 'revertedInk';

export interface GuestAvatarProps {
  variant?: GuestIconVariant;
  tone?: GuestAvatarTone;
  size?: number;
  style?: React.CSSProperties;
}

const TONE_STYLES: Record<GuestAvatarTone, React.CSSProperties> = {
  ink:   { background: '#111111', color: '#C6F23A' },
  revertedInk:   { background: '#C6F23A', color: '#111111' },
  soft:  { background: '#3A3935', color: '#FFFFFF' },
  empty: { background: '#EBEBE6', color: '#5C5C57', border: '1px dashed rgba(17,17,17,0.3)' },
};

export function GuestAvatar({
  variant = 'interrogacao',
  tone = 'ink',
  size = 26,
  style,
}: GuestAvatarProps) {
  return (
    <span
      style={{
        width: size,
        height: size,
        borderRadius: '50%',
        display: 'grid',
        placeItems: 'center',
        flexShrink: 0,
        ...TONE_STYLES[tone],
        ...style,
      }}
      aria-label="Convidado"
    >
      <GuestIcon variant={variant} size={Math.round(size * 0.54)} />
    </span>
  );
}

export default GuestIcon;
